import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import sharedResearchService, { SharedResearchItem, SharedResearchMessage } from '../services/sharedResearchService';
import { useRISViewer } from '../contexts/RISViewerContext';
import { INHALTE_GESPERRT } from '../config/ebenen';
import { antwortHtml } from '../utils/antwortFormat';
import { Dialog, Field, FormError } from '../administration/components';
import { datumZeit } from '../utils/datum';

// Paket 10 B10 Oberfläche (KAN-26 bis KAN-30, Plan 10-N:1058-1101): Momentaufnahmen teilen, lesen, daraus einen eigenen
// Verlauf starten, zurückziehen. Wortwahl nach F10-11 (MK-1c): kein Begriff der Organisation mit Artikel, Sätze nennen
// ihren Namen. In einer Sicht antworten die Wege 403 (VE-25); das Panel zeigt dann INHALTE_GESPERRT statt eines Fehlers.
// F10-26a (Korrekturlauf 09.10.2026): eine Momentaufnahme der Fassung 2 liest sich wie der Chat, mit Abschnitten „Frage“
// (reiner Text) und „Antwort“ (utils/antwortFormat.ts, Quellen als Links); ältere Momentaufnahmen bleiben Rohtext.

export const NICHT_MEHR_DA = 'Diese Momentaufnahme ist nicht mehr verfügbar.';
const TITEL_REGEL = 'Bitte einen Titel mit 3 bis 200 Zeichen angeben.';

function antwort(error: unknown): { status?: number; code?: string; text?: string } {
  const response = (error as { response?: { status?: number; data?: { code?: unknown; error?: unknown } } } | null)?.response;
  return {
    status: response?.status,
    code: typeof response?.data?.code === 'string' ? response.data.code : undefined,
    text: typeof response?.data?.error === 'string' ? response.data.error : undefined,
  };
}

// VE-25, M-17.6: lesen antwortet in einer Sicht OPERATOR_VIEW_SCOPE, schreiben OPERATOR_VIEW_READ_ONLY.
export function istSichtSperre(error: unknown): boolean {
  const { status, code } = antwort(error);
  return status === 403 && (code === 'OPERATOR_VIEW_SCOPE' || code === 'OPERATOR_VIEW_READ_ONLY');
}

// Jede Absage des Servers trägt einen deutschen Satz in `error` (K10-13, Hinweis 9); 404 sagt nur „nicht gefunden“.
export function fehlerText(error: unknown, rueckfall: string, nichtGefunden: string = NICHT_MEHR_DA): string {
  if (istSichtSperre(error)) return INHALTE_GESPERRT;
  const { status, text } = antwort(error);
  if (status === 404) return nichtGefunden;
  return text || rueckfall;
}

// Dieselbe Grenze wie der Server (sharedResearch.ts:26): 3 bis 200 Zeichen nach trim.
export function titelPruefen(titel: string): string | null {
  const laenge = titel.trim().length;
  return laenge < 3 || laenge > 200 ? TITEL_REGEL : null;
}

export function standVom(createdAt: string): string {
  return `Stand vom ${datumZeit(createdAt)}`;
}

const knopf = 'px-3 py-2 text-sm rounded-xl border border-[rgb(var(--cl-ink-rgb)/0.2)] dark:border-gray-600 text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors';
const hauptKnopf = 'px-3 py-2 text-sm rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors';
const fehlerKasten = 'p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400';

interface EintragProps {
  item: SharedResearchItem;
  organizationName: string;
  offen: boolean;
  inhalt: string | null;
  // F10-26a: Fragen und Antworten einer Momentaufnahme der Fassung 2; ohne sie zeigt der Eintrag `inhalt` als Text.
  nachrichten?: SharedResearchMessage[] | null;
  bestaetigen: boolean;
  beschaeftigt: boolean;
  onLesen: () => void;
  onWeiter: () => void;
  onZurueckziehen: () => void;
  onBestaetigen: () => void;
  onAbbrechen: () => void;
  // F10-26a: „Dokument anzeigen“ in einer Antwort; Frage und Antwort gehen als Zusammenhang an die Dokumentansicht.
  onDokument?: (dokumentId: string, frage: string, antwort: string) => void;
  // W2-A3: ist die Recherche gesperrt, die Liste aber lesbar (etwa Recherchen im Monat aufgebraucht), steht hier der Grund
  // wie unter „Neue Recherche“; „Eigene Recherche daraus starten“ ist dann gesperrt, „Lesen“ und das Zurückziehen bleiben.
  sperrgrund?: string | null;
}

// Schlusslauf 3, N10: Fließtext in der Schriftfarbe (--cl-ink); text-primary ist die Linkfarbe, die Antwort stand blau wie ihre Links.
const standRahmen = 'mt-2 max-h-72 overflow-y-auto break-words rounded-lg border border-[rgb(var(--cl-ink-rgb)/0.15)] dark:border-gray-600 p-3 text-sm text-[rgb(var(--cl-ink-rgb))] dark:text-white';

// KAN-30: Titel, Einsteller, Stand-Datum; der Veraltungshinweis, sobald die Quelle seither weitergeführt wurde (stale).
export function SharedResearchEintrag({ item, organizationName, offen, inhalt, nachrichten, bestaetigen, beschaeftigt,
  onLesen, onWeiter, onZurueckziehen, onBestaetigen, onAbbrechen, onDokument, sperrgrund }: EintragProps) {
  const sperrId = `momentaufnahme-sperre-${item.id}`;
  // F10-26a: Klicks in einer Antwort. RIS-Links öffnen ein neues Fenster (die Dokumentansicht für RIS gibt es nur auf der
  // Rechercheseite); „Dokument anzeigen“ übergibt die Kennung mit der Frage davor und der Antwort. Andere Links tragen
  // target="_blank" schon im HTML.
  const quelleKlick = (event: React.MouseEvent<HTMLDivElement>, index: number) => {
    const ziel = event.target as HTMLElement;
    const dokument = ziel.closest('a[data-document-id]');
    if (dokument) {
      event.preventDefault();
      const dokumentId = dokument.getAttribute('data-document-id');
      const frage = (nachrichten ?? []).slice(0, index).filter(nachricht => nachricht.rolle === 'user').pop()?.text ?? '';
      if (dokumentId) onDokument?.(dokumentId, frage, nachrichten?.[index]?.text ?? '');
      return;
    }
    const ris = ziel.closest('a[data-ris-url]');
    if (ris) {
      event.preventDefault();
      const url = ris.getAttribute('data-ris-url');
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  return (
    <li className="py-4 border-b border-[rgb(var(--cl-ink-rgb)/0.1)] dark:border-gray-700 last:border-b-0">
      <h3 className="text-base font-semibold text-primary dark:text-white break-words">{item.title}</h3>
      <p className="mt-1 text-sm text-muted">{`Geteilt von ${item.authorName} · ${standVom(item.createdAt)}`}</p>
      {item.stale && (
        <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">Die Quelle wurde seither weitergeführt.</p>
      )}
      {offen && (
        <div className="mt-3">
          <p className="text-sm text-muted">Dies ist eine Momentaufnahme, nicht die laufende Recherche.</p>
          {inhalt === null ? (
            <p className="mt-2 text-sm text-muted">Wird geladen …</p>
          ) : nachrichten ? (
            <div className={`${standRahmen} space-y-4`}>
              {nachrichten.map((nachricht, index) => nachricht.rolle === 'user' ? (
                <div key={index}>
                  <h4 className="text-sm font-semibold text-muted">Frage</h4>
                  <p className="mt-1 whitespace-pre-wrap">{nachricht.text}</p>
                </div>
              ) : (
                <div key={index}>
                  <h4 className="text-sm font-semibold text-muted">Antwort</h4>
                  <div className="mt-1" onClick={event => quelleKlick(event, index)} dangerouslySetInnerHTML={antwortHtml(nachricht.text)} />
                </div>
              ))}
            </div>
          ) : (
            <div className={`${standRahmen} whitespace-pre-wrap`}>
              {inhalt}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={knopf} aria-expanded={offen} onClick={onLesen}>{offen ? 'Zuklappen' : 'Lesen'}</button>
        <button type="button" className={hauptKnopf} disabled={beschaeftigt || !!sperrgrund} aria-describedby={sperrgrund ? sperrId : undefined} onClick={onWeiter}>Eigene Recherche daraus starten</button>
        {/* F10-19: zurückziehen darf, wer eingestellt hat oder wer hier Mitglieder verwaltet (canRevoke vom Server). */}
        {item.canRevoke && !bestaetigen && (
          <button type="button" className={knopf} disabled={beschaeftigt} onClick={onZurueckziehen}>Freigabe zurückziehen</button>
        )}
      </div>
      {sperrgrund && <p role="note" id={sperrId} className="mt-2 text-sm text-muted">{sperrgrund}</p>}
      {item.canRevoke && bestaetigen && (
        <div role="group" aria-label="Freigabe zurückziehen" className="mt-3 rounded-lg border border-[rgb(var(--cl-ink-rgb)/0.15)] dark:border-gray-600 p-3">
          <p className="text-sm text-primary dark:text-white">
            {`Danach sieht niemand in „${organizationName}“ diese Momentaufnahme mehr. Bereits gestartete eigene Recherchen bleiben bestehen.`}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={hauptKnopf} disabled={beschaeftigt} onClick={onBestaetigen}>Zurückziehen</button>
            <button type="button" className={knopf} disabled={beschaeftigt} onClick={onAbbrechen}>Abbrechen</button>
          </div>
        </div>
      )}
    </li>
  );
}

interface PanelProps {
  organizationName: string;
  onClose: () => void;
  onContinue: (sessionId: string) => void;
  // W2-A3: der Grund der gesperrten „Neue Recherche“ (AppSidebar: researchLock), sonst null.
  sperrgrund?: string | null;
}

// KAN-27 bis KAN-30: die Liste „Geteilte Momentaufnahmen“ als Dialog über der Anwendung. DR-13 UI-19: der Dialog der Anwendung
// (administration/components.tsx) mit Escape, Schließen-Knopf, Fokus und Scrollsperre; das Portal hält ihn außerhalb der Leiste.
export default function SharedResearchPanel({ organizationName, onClose, onContinue, sperrgrund = null }: PanelProps) {
  const navigate = useNavigate();
  const { openDocumentViewer } = useRISViewer();
  const [items, setItems] = useState<SharedResearchItem[] | null>(null);
  const [gesperrt, setGesperrt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [inhalte, setInhalte] = useState<Record<string, { text: string; nachrichten: SharedResearchMessage[] | null }>>({});
  const [bestaetigen, setBestaetigen] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      setItems(await sharedResearchService.list());
    } catch (error) {
      if (istSichtSperre(error)) setGesperrt(true);
      else setFehler(fehlerText(error, 'Die geteilten Momentaufnahmen konnten nicht geladen werden.'));
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // Eine Absage beim Lesen, Weiterarbeiten oder Zurückziehen: Sicht → Sperrsatz; nicht mehr da → Satz und frische Liste.
  const absage = async (error: unknown, rueckfall: string) => {
    if (istSichtSperre(error)) { setGesperrt(true); return; }
    if (antwort(error).status === 404) {
      setMeldung(NICHT_MEHR_DA);
      await laden();
      return;
    }
    setFehler(fehlerText(error, rueckfall));
  };

  const lesen = async (id: string) => {
    setMeldung(null);
    if (offen === id) { setOffen(null); return; }
    setOffen(id);
    if (inhalte[id] !== undefined) return;
    try {
      const detail = await sharedResearchService.get(id);
      setInhalte(vorher => ({ ...vorher, [id]: { text: detail.content, nachrichten: detail.nachrichten ?? null } }));
    } catch (error) {
      setOffen(null);
      await absage(error, 'Die Momentaufnahme konnte nicht geladen werden.');
    }
  };

  // F10-26a: „Dokument anzeigen“ öffnet die Dokumentansicht der Rechercheseite (App.tsx: nur dort eingebunden); das Panel
  // schließt dabei. W2-A3: ohne Recherchezugang zeigt die Rechercheseite die Sperre statt der Ansicht; dann bleibt das
  // Panel stehen und nennt den Grund.
  const dokumentOeffnen = (dokumentId: string, frage: string, antwortText: string) => {
    if (sperrgrund) { setMeldung(sperrgrund); return; }
    onClose();
    navigate('/ris');
    void openDocumentViewer(dokumentId, frage, antwortText);
  };

  // KAN-28: der neue Verlauf gehört dem Leser; die Hülle öffnet ihn wie einen Klick auf einen Eintrag der Leiste.
  const weiter = async (id: string) => {
    setBeschaeftigt(id); setFehler(null); setMeldung(null);
    try {
      onContinue(await sharedResearchService.continueFrom(id));
    } catch (error) {
      await absage(error, 'Aus der Momentaufnahme konnte keine eigene Recherche gestartet werden.');
    } finally {
      setBeschaeftigt(null);
    }
  };

  const zurueckziehen = async (id: string) => {
    setBeschaeftigt(id); setFehler(null); setMeldung(null);
    try {
      await sharedResearchService.revoke(id);
      setBestaetigen(null);
      setOffen(vorher => (vorher === id ? null : vorher));
      setMeldung('Freigabe zurückgezogen.');
      await laden();
    } catch (error) {
      setBestaetigen(null);
      await absage(error, 'Die Freigabe konnte nicht zurückgezogen werden.');
    } finally {
      setBeschaeftigt(null);
    }
  };

  // F-SL6-1: der Dialog der Anwendung ohne Portal; showModal legt ihn in die oberste Ebene, er nimmt den Zoom der Hülle mit.
  return (
    <Dialog title="Geteilte Momentaufnahmen" onClose={onClose} breit>
      {!gesperrt && (
        <p className="text-sm text-muted">
          Was Personen in „{organizationName}“ geteilt haben. Jede Momentaufnahme zeigt den Stand beim Teilen, nicht die laufende Recherche. „Eigene Recherche daraus starten“ legt eine neue Recherche für Sie an; die Quelle bleibt unverändert.
        </p>
      )}
      {gesperrt ? (
        <p role="status" className="py-4 text-sm text-muted">{INHALTE_GESPERRT}</p>
      ) : (
        <>
          {meldung && <p role="status" className="mt-4 text-sm text-primary dark:text-white">{meldung}</p>}
          {fehler && <p role="alert" className={`mt-4 ${fehlerKasten}`}>{fehler}</p>}
          {items === null ? (
            !fehler && <p className="py-4 text-sm text-muted">Wird geladen …</p>
          ) : items.length === 0 ? (
            <p className="py-4 text-sm text-muted">
              In „{organizationName}“ ist noch keine Momentaufnahme geteilt. Eine eigene Recherche teilen Sie in der Liste Ihrer Recherchen über „Momentaufnahme teilen“.
            </p>
          ) : (
            <ul>
              {items.map(item => (
                <SharedResearchEintrag
                  key={item.id}
                  item={item}
                  organizationName={organizationName}
                  offen={offen === item.id}
                  inhalt={inhalte[item.id]?.text ?? null}
                  nachrichten={inhalte[item.id]?.nachrichten ?? null}
                  bestaetigen={bestaetigen === item.id}
                  beschaeftigt={beschaeftigt === item.id}
                  onLesen={() => lesen(item.id)}
                  onWeiter={() => weiter(item.id)}
                  onZurueckziehen={() => setBestaetigen(item.id)}
                  onBestaetigen={() => zurueckziehen(item.id)}
                  onAbbrechen={() => setBestaetigen(null)}
                  onDokument={dokumentOeffnen}
                  sperrgrund={sperrgrund}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </Dialog>
  );
}

interface TeilenProps {
  sessionId: string;
  sessionTitle: string;
  organizationName: string;
  onClose: () => void;
}

// KAN-26: der Einstelldialog, Titel vorbelegt mit dem Titel der Recherche.
export function MomentaufnahmeTeilenDialog({ sessionId, sessionTitle, organizationName, onClose }: TeilenProps) {
  const [titel, setTitel] = useState(sessionTitle.trim().slice(0, 200));
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  const absenden = async (event: React.FormEvent) => {
    event.preventDefault();
    const pruefung = titelPruefen(titel);
    if (pruefung) { setFehler(pruefung); return; }
    setFehler('');
    setLaeuft(true);
    try {
      await sharedResearchService.publish(sessionId, titel.trim());
      toast.success(`In „${organizationName}“ geteilt. Sie finden die Momentaufnahme unter „Geteilte Momentaufnahmen“.`);
      onClose();
    } catch (error) {
      setFehler(fehlerText(error, 'Die Momentaufnahme konnte nicht geteilt werden.', 'Diese Recherche wurde nicht gefunden.'));
      setLaeuft(false);
    }
  };

  // DR-13 UI-19: der Dialog der Anwendung mit Schließen-Knopf; Escape und Schließen ruhen, solange geteilt wird. Der Fokus geht
  // danach an den Knopf „…“ der Zeile zurück (AppSidebar hält ihn sichtbar, solange der Dialog offen ist).
  // F-SL6-1: ohne Portal, wie oben.
  return (
    <Dialog title="Momentaufnahme teilen" onClose={onClose} busy={laeuft}>
      <p>Sichtbar für alle Personen in „{organizationName}“.</p>
      <p className="adm-muted">
        Geteilt wird der heutige Stand dieser Recherche. Ihre Recherche bleibt privat und läuft weiter; spätere Nachrichten werden nicht geteilt. Sie können die Freigabe jederzeit zurückziehen; das kann auch, wer in „{organizationName}“ Mitglieder verwaltet.
      </p>
      <form onSubmit={absenden} noValidate>
        <Field label="Titel" hint="3 bis 200 Zeichen."><input type="text" value={titel} onChange={event => setTitel(event.target.value)} maxLength={200} required autoFocus /></Field>
        <FormError message={fehler} />
        <footer>
          <button type="button" className="adm-button" disabled={laeuft} onClick={onClose}>Abbrechen</button>
          <button type="submit" className="adm-button adm-primary" disabled={laeuft}>{laeuft ? 'Wird geteilt …' : 'Teilen'}</button>
        </footer>
      </form>
    </Dialog>
  );
}
